// Custom chart layers for PostureTrendChart

export const GradientDefs = ({ colors }) => {
  return (
    <defs>
      <linearGradient id="postureGradient" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor={colors.primary_200} stopOpacity={0.3} />
        <stop offset="100%" stopColor={colors.primary_200} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
};

export const GradientArea = ({
  series,
  areaGenerator,
  xScale,
  yScale,
  innerHeight,
  currentMonthIndex,
}) => {
  const yourPostureSeries = series.find((s) => s.id === "Your Posture");

  if (!yourPostureSeries) return null;

  const { data } = yourPostureSeries;

  const todayIndex =
    currentMonthIndex >= 0 ? currentMonthIndex : data.length - 1;
  const historicalData = data
    .slice(0, todayIndex + 1)
    .filter((d) => d.data.y !== null && d.data.y !== undefined);

  if (historicalData.length === 0) return null;

  const areaData = historicalData.map((d) => ({
    x: xScale(d.data.x),
    y0: yScale(d.data.y),
    y1: innerHeight,
  }));

  const areaPath = areaGenerator
    .x((d) => d.x)
    .y0((d) => d.y0)
    .y1((d) => d.y1)(areaData);

  return <path d={areaPath} fill="url(#postureGradient)" opacity={1} />;
};

export const DashedSolidLine = ({
  series,
  lineGenerator,
  xScale,
  yScale,
  currentMonthIndex,
  lastMonthDate,
  isWeeklyView,
}) => {
  return series.map(({ id, data, color }) => {
    const todayIndex =
      currentMonthIndex >= 0 ? currentMonthIndex : data.length - 1;

    const historicalData = data
      .slice(0, todayIndex + 1)
      .filter((d) => d.data.y !== null && d.data.y !== undefined);

    if (historicalData.length === 0) {
      return null;
    }

    const lastPoint = historicalData[historicalData.length - 1];
    const recentPoints = historicalData.slice(-3);

    if (recentPoints.length >= 2) {
      const avgChange =
        recentPoints.reduce((acc, point, idx) => {
          if (idx === 0) return 0;
          return acc + (point.data.y - recentPoints[idx - 1].data.y);
        }, 0) /
        (recentPoints.length - 1);

      const baseDate = lastMonthDate || new Date();
      const projectedPoints = [lastPoint];

      for (let i = 1; i <= 2; i++) {
        const futureDate = new Date(baseDate);
        let futureLabel;

        if (isWeeklyView) {
          futureDate.setDate(futureDate.getDate() + i * 7);
          futureLabel = futureDate.toLocaleString("default", {
            month: "short",
            day: "numeric",
          });
        } else {
          futureDate.setMonth(futureDate.getMonth() + i);
          futureLabel = futureDate.toLocaleString("default", {
            month: "short",
            year: "numeric",
          });
        }

        const projectedY = lastPoint.data.y + avgChange * i;
        projectedPoints.push({
          data: { x: futureLabel, y: projectedY },
        });
      }

      return (
        <g key={id}>
          <path
            d={lineGenerator(
              historicalData.map((d) => ({
                x: xScale(d.data.x),
                y: yScale(d.data.y),
              }))
            )}
            fill="none"
            stroke={color}
            strokeWidth={2}
          />

          <path
            d={lineGenerator(
              projectedPoints.map((d) => ({
                x: xScale(d.data.x),
                y: yScale(d.data.y),
              }))
            )}
            fill="none"
            stroke={color}
            strokeWidth={2}
            strokeDasharray="8, 8"
          />
        </g>
      );
    }

    return (
      <g key={id}>
        <path
          d={lineGenerator(
            data.map((d) => ({
              x: xScale(d.data.x),
              y: yScale(d.data.y),
            }))
          )}
          fill="none"
          stroke={color}
          strokeWidth={2}
        />
      </g>
    );
  });
};

export const VerticalAnnotations = ({
  data,
  xScale,
  yScale,
  height,
  colors,
  timeRange,
}) => {
  // Safety check
  if (
    !data ||
    !data.data ||
    !data.data[0] ||
    !data.data[0].data ||
    !xScale ||
    !height ||
    !colors
  ) {
    return null;
  }

  const annotations = [];
  const currentDate = new Date();
  const isWeeklyView = timeRange === "30D";

  // Calculate the current period label based on the time range
  let currentPeriodLabel;
  if (isWeeklyView) {
    // For weekly view, get the start of current week
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);

    currentPeriodLabel = startOfWeek.toLocaleString("default", {
      month: "short",
      day: "numeric",
    });
  } else {
    // For monthly view, get current month
    currentPeriodLabel = currentDate.toLocaleString("default", {
      month: "short",
      year: "numeric",
    });
  }

  // Try to find the current period in the chart data
  const chartData = data.data[0]?.data || [];
  const currentDataPoint = chartData.find(
    (point) => point.x === currentPeriodLabel
  );

  if (currentDataPoint) {
    const currentXPos = xScale(currentPeriodLabel);

    // Only add if we got a valid position
    if (!isNaN(currentXPos)) {
      annotations.push({
        x: currentXPos,
        label: "Today",
      });
    }
  }

  // If no valid annotations, return null
  if (annotations.length === 0) return null;

  return (
    <g>
      {annotations.map((annotation, index) => (
        <g key={`annotation-${index}`}>
          <line
            x1={annotation.x}
            x2={annotation.x}
            y1={0}
            y2={height}
            stroke={colors.grey_500}
            strokeWidth={0.5}
            strokeDasharray="5 5"
          />
          <text
            x={annotation.x}
            y={10}
            textAnchor="middle"
            fill={colors.grey_600}
            fontSize={10}
            fontWeight={500}
            fontFamily="Poppins"
          >
            {annotation.label}
          </text>
        </g>
      ))}
    </g>
  );
};
